import { HttpRequest } from '../utils/httpClient';

import { createLogger } from '../utils/logger';
const logger = createLogger('services');

/**
 * Role structure from backend
 */
export interface Role {
  id: string;
  name: string;
  description?: string;
  status?: string;
  createdAt?: number;
  updatedAt?: number;
}

/**
 * Service for fetching roles from the backend API
 */
class RoleService {
  private httpClient: HttpRequest;
  private rolesCache: Map<string, string> = new Map(); // roleName -> roleId

  constructor() {
    const API_BASE_URL = process.env.REACT_APP_API_HOST || 'https://api.dev.mind.creai.cloud';
    const rolesBaseUrl = `${API_BASE_URL}/permissions`;
    this.httpClient = new HttpRequest(rolesBaseUrl);
  }

  /**
   * Fetch all roles from the backend
   *
   * @returns Array of roles
   */
  async getAllRoles(): Promise<Role[]> {
    try {
      const response = await this.httpClient.get('role');

      if (response.status !== 200) {
        logger.error(`Failed to fetch roles: ${response.status} - ${response.statusText}`);
        return [];
      }

      const data = response.data;

      // Handle both response formats
      const roles = data.roles || data || [];

      // Cache the mapping
      roles.forEach((role: Role) => {
        this.rolesCache.set(role.name.toLowerCase(), role.id);
      });

      return roles;
    } catch (error) {
      logger.error('Error fetching roles:', error);
      return [];
    }
  }

  /**
   * Get roleId by role name
   *
   * @param roleName - The role name (e.g., 'admin', 'user')
   * @returns The role UUID or null if not found
   */
  async getRoleIdByName(roleName: string): Promise<string | null> {
    const normalizedName = roleName.toLowerCase().trim();

    // Check cache first
    if (this.rolesCache.has(normalizedName)) {
      return this.rolesCache.get(normalizedName) || null;
    }

    // Fetch from backend if not cached
    await this.getAllRoles();

    return this.rolesCache.get(normalizedName) || null;
  }

  /**
   * Get all available role names and IDs
   *
   * @returns Map of roleName -> roleId
   */
  async getRoleMapping(): Promise<Map<string, string>> {
    if (this.rolesCache.size === 0) {
      await this.getAllRoles();
    }
    return this.rolesCache;
  }

  /**
   * Clear the roles cache (useful for testing or when roles change)
   */
  clearCache(): void {
    this.rolesCache.clear();
  }
}

// Export singleton instance
export const roleService = new RoleService();
